import traverse from 'traverse';
import {
    FieldCount,
    ListType,
    SectionType,
    WorksheetSchema,
    WorksheetSchemaProperty,
    WorksheetSchemaField,
    WorksheetSchemaSection,
    WorksheetSchemaSectionGroup,
    WorksheetSchemaList,
    WorksheetSchemaListItem,
    WorksheetSchemaBase,
    CWTRef,
    ConditionIf,
    ConditionAnd,
    ConditionOr,
    CWTSchemaCalculation,
    CWTSchemaSection,
    CWTSchemaSectionGroup,
    WorksheetSchemaTable,
    TableType,
    WorksheetSchemaTableRow,
    TableRowStyles,
    CWTSchemaTableRow,
    CWTSchemaField,
    CWTSchemaTable,
    CWTSchemaPropertySummary,
    FieldType,
    CWTSchemaPropertiesSummary,
} from '../../../../types/custom-worksheet-v2';
import { get, set } from 'lodash';
import { current } from '@reduxjs/toolkit';
import { isCWTSchemaField } from '../../../custom-worksheet-template/store/utils';
import { ReferenceMap } from './slice';

/**
 * Checks if a schema property is a table
 *
 * @param property
 * @returns property is WorksheetSchemaTable
 */
export function isTable(property: WorksheetSchemaProperty): property is WorksheetSchemaTable {
    return property.type === TableType.TABLE;
}

/**
 * Checks if a schema property is a table row
 *
 * @param property
 * @returns property is WorksheetSchemaTableRow
 */
export function isTableRow(property: WorksheetSchemaProperty): property is WorksheetSchemaTableRow {
    return property.type === TableType.TABLE_ROW;
}

/**
 * Checks if a schema property is a list
 *
 * @param property
 * @returns property is WorksheetSchemaList
 */
export function isList(property: WorksheetSchemaProperty): property is WorksheetSchemaList {
    return property.type === ListType.LIST;
}

/**
 * Checks if a schema property is a list item
 *
 * @param property
 * @returns property is WorksheetSchemaListItem
 */
export function isListItem(property: WorksheetSchemaProperty): property is WorksheetSchemaListItem {
    return property.type === ListType.LIST_ITEM;
}

/**
 * Checks if a schema property is a section group
 *
 * @param property
 * @returns property is WorksheetSchemaSectionGroup
 */
export function isSectionGroup(property: WorksheetSchemaProperty): property is WorksheetSchemaSectionGroup {
    return property.type === SectionType.SECTION_GROUP;
}

/**
 * Checks if a schema property is a section
 *
 * @param property
 * @returns property is WorksheetSchemaSection
 */
export function isSection(property: WorksheetSchemaProperty): property is WorksheetSchemaSection {
    return property.type === SectionType.SECTION;
}

/**
 * Checks if a schema property is a field
 *
 * @param property
 * @returns property is WorksheetSchemaField
 */
export function isField(property: WorksheetSchemaProperty): property is WorksheetSchemaField {
    return (
        isObject(property) &&
        !isSectionGroup(property) &&
        !isSection(property) &&
        !isList(property) &&
        !isListItem(property) &&
        !isTable(property) &&
        !isTableRow(property)
    );
}

export function isProperty(p: unknown): p is WorksheetSchemaProperty {
    return typeof p === 'object' && p !== null && !Array.isArray(p) && 'id' in p;
}

/**
 * Get schema property by its id
 *
 * @param schema WorksheetSchema
 * @param id Property id
 * @returns WorksheetSchemaProperty
 */
export function getProperty(schema: WorksheetSchema, id: string): WorksheetSchemaProperty | null {
    const property = get(schema, id);

    if (!property) {
        return null;
    }

    return property;
}

/**
 * Get schema section by its id
 *
 * @param schema WorksheetSchema
 * @param id Section id
 * @returns WorksheetSchemaSection
 */
export function getSection(schema: WorksheetSchema, id: string): WorksheetSchemaSection {
    return get(schema, id);
}

/**
 * Get schema field by its id
 *
 * @param schema WorksheetSchema
 * @param id Field id or ref
 * @returns WorksheetSchemaField
 */
export function getField(schema: WorksheetSchema, id: string | CWTRef): WorksheetSchemaField | null {
    if (typeof id === 'string') {
        return get(schema, id) || null;
    }

    if ('ref' in id) {
        return get(schema, id.ref.substring(1)) || null;
    }
    return null;
}

/**
 * Get schema field value by its id
 *
 * @param schema WorksheetSchema
 * @param id Field id
 * @returns value
 */
export function getFieldValue(schema: WorksheetSchema, id: string | CWTRef): unknown {
    const field = getField(schema, id);

    if (!field) {
        return null;
    }

    if (field.value !== null) {
        return field.value;
    }

    return null;
}

export function setFieldValue(schema: WorksheetSchema, id: string, value: unknown): WorksheetSchema {
    const field = getField(schema, id);

    // If the field type is money or accounting and it has a decimal due to micro-cents round to nearest cents
    if (typeof value === 'number' && (field?.type === FieldType.MONEY || field?.type === FieldType.ACCOUNTING)) {
        value = Math.round(value);
    }

    return set(schema, `${id}.value`, value);
}

/**
 * Return properties with remaining field items
 *
 * @param section WorksheetSchemaListItem | WorksheetSchemaSection | WorksheetSchemaSectionGroup
 * @returns { [key: string]: WorksheetSchemaSectionGroup | WorksheetSchemaSection }
 */
export function filterIncompleteProperties(
    section: WorksheetSchemaListItem | WorksheetSchemaSection | WorksheetSchemaSectionGroup | WorksheetSchemaTableRow,
    showHiddenFields: boolean
): {
    [key: string]:
        | WorksheetSchemaSectionGroup
        | WorksheetSchemaSection
        | WorksheetSchemaField
        | (WorksheetSchemaField & { styles: TableRowStyles });
} {
    const props: { [key: string]: WorksheetSchemaSectionGroup | WorksheetSchemaSection | WorksheetSchemaField } = {};

    for (const key in section.properties) {
        const property = section.properties[key];

        if (isSection(property)) {
            const { remaining } = getFieldCount(property, showHiddenFields);
            if (remaining > 0) {
                const _props: { [key: string]: WorksheetSchemaField } = {};

                for (const key in property.properties) {
                    const field = property.properties[key];

                    if (field.value === null) {
                        _props[key] = field;
                    }
                }

                props[key] = { ...property, properties: _props };
            }
        } else if (isField(property)) {
            if (property.hidden && !showHiddenFields) {
                continue;
            }
            if (property.value === null) {
                props[key] = property;
            }
        } else if (isSectionGroup(property)) {
            const { remaining } = getFieldCount(property, showHiddenFields);
            if (remaining > 0) {
                props[key] = { ...property, properties: filterIncompleteProperties(property, showHiddenFields) };
            }
        }
    }
    return props;
}

/**
 * Filter out schema with properties that match with search query
 *
 * @param schema Worksheet schema
 * @param searchQuery Search query
 * @returns Filtered schema with properties that match with search query
 */
export function searchSchema(schema: WorksheetSchema, searchQuery: string, showHidden: boolean): WorksheetSchema {
    const _schema: WorksheetSchema = { version: schema.version, properties: {}, reviewConditions: [] };

    for (const key in schema.properties) {
        const property = schema.properties[key];

        if (isList(property)) {
            const filteredListItems = searchList(property, searchQuery, showHidden);

            if (filteredListItems) {
                _schema.properties[key] = filteredListItems;
            }
        }

        if (isSectionGroup(property)) {
            const filteredSectionGroup = searchSectionGroup(property, searchQuery, showHidden);

            if (filteredSectionGroup) {
                _schema.properties[key] = filteredSectionGroup as WorksheetSchemaSectionGroup;
            }
        }

        if (isSection(property)) {
            const filteredSection = searchFields(property, searchQuery, showHidden);

            if (filteredSection) {
                _schema.properties[key] = filteredSection;
            }
        }
        if (isField(property)) {
            if (property.name.toLowerCase().includes(searchQuery.toLowerCase())) {
                if (property.hidden && !showHidden) {
                    continue;
                }
                _schema.properties[key] = property;
            }
        }
    }

    return _schema;
}

function searchTable(table: WorksheetSchemaTable, searchQuery: string, showHidden: boolean): WorksheetSchemaTable | null {
    const row = table.row;

    const match = searchFields(row, searchQuery, showHidden);
    if (match) {
        return table;
    }
    return null;
}

/**
 * Search for list items within a list that matches with search query
 *
 * @param list WorksheetSchemaList
 * @param searchQuery Search query
 * @returns Filtered worksheet schema list if at least one item matches
 */
function searchList(list: WorksheetSchemaList, searchQuery: string, showHidden: boolean): WorksheetSchemaList | undefined {
    const listItems: WorksheetSchemaListItem[] = [];

    for (const listItem of list.properties) {
        const props = searchSectionGroup(listItem, searchQuery, showHidden);

        if (props) {
            listItems.push(props as WorksheetSchemaListItem);
        }
    }

    // Return list with at least one matching list item
    if (listItems.length > 0) {
        return { ...list, properties: listItems };
    }
}

/**
 * Search for section group and list item that matches with search query
 *
 * @param item WorksheetSchemaSectionGroup | WorksheetSchemaListItem
 * @param searchQuery Search query
 * @returns Filtered WorksheetSchemaSectionGroup or WorksheetSchemaListItem if at least one item matches
 */
function searchSectionGroup(
    item: WorksheetSchemaSectionGroup | WorksheetSchemaListItem,
    searchQuery: string,
    showHidden: boolean
): WorksheetSchemaSectionGroup | WorksheetSchemaListItem | WorksheetSchemaTable | undefined {
    const props: { [key: string]: WorksheetSchemaSectionGroup | WorksheetSchemaSection | WorksheetSchemaTable | WorksheetSchemaField } = {};

    for (const key in item.properties) {
        const property = item.properties[key];

        if (isTable(property)) {
            const table = searchTable(property, searchQuery, showHidden);
            if (table) {
                props[key] = table;
            }
        }

        // If property is a section, search for fields that match the search query
        else if (isSection(property)) {
            const filteredSection = searchFields(property, searchQuery, showHidden);

            if (filteredSection) {
                props[key] = filteredSection;
            }
        } else if (isField(property)) {
            if (property.name.toLowerCase().includes(searchQuery.toLowerCase())) {
                props[key] = property;
            }
        }

        // If property is a section group, repeat the function to search for children (section group/section) that matches the search query
        else if (isSectionGroup(property) || isListItem(property)) {
            const filteredProperties = searchSectionGroup(property, searchQuery, showHidden);

            if (filteredProperties && !isTable(filteredProperties) && !isField(filteredProperties)) {
                // @ts-ignore
                props[key] = { ...property, properties: filteredProperties.properties };
            }
        }
    }

    // Return section group or list item with at least one matching child property
    if (Object.keys(props).length > 0) {
        // @ts-ignore
        return { ...item, properties: props };
    }
}

/**
 * Search for fields within a section that matches with search query
 *
 * @param section WorksheetSchemaSection
 * @param searchQuery Search query
 * @returns Filtered WorksheetSchemaSection if at least one item matches
 */
function searchFields<T extends { properties: { [key: string]: WorksheetSchemaField | CWTSchemaField } }>(
    section: T,
    searchQuery: string,
    showHidden: boolean
): T | undefined {
    const props: { [key: string]: WorksheetSchemaField | CWTSchemaField } = {};

    for (const key in section.properties) {
        const field = section.properties[key];

        if (field.name.toLowerCase().includes(searchQuery.toLowerCase().trim())) {
            if (field.hidden && !showHidden) {
                continue;
            }
            props[key] = field;
        }
    }

    // Return section with at least one matching field
    if (Object.keys(props).length > 0) {
        return { ...section, properties: props };
    }
}
/**
 * Gets the total number and remaining fields for a given section.
 *
 * @param section WorksheetSchemaList | WorksheetSchemaListItem | WorksheetSchemaSection | WorksheetSchemaSectionGroup
 * @returns total number of fields, completed number of fields, remaining number of fields
 */
export function getFieldCount(
    section:
        | WorksheetSchema
        | WorksheetSchemaList
        | WorksheetSchemaListItem
        | WorksheetSchemaSection
        | WorksheetSchemaSectionGroup
        | WorksheetSchemaTable
        | WorksheetSchemaTableRow
        | null,
    showHiddenFields: boolean
): FieldCount {
    let total = 0;
    let remaining = 0;
    if (!section) {
        return { total: 0, remaining: 0, completed: 0 };
    }

    let allProperties: {
        [key: string]: WorksheetSchemaSection | WorksheetSchemaSectionGroup | WorksheetSchemaField | WorksheetSchemaTable;
    }[] = [];

    // Allow the whole schema to be passed in
    if ('version' in section) {
        for (const prop in section.properties) {
            const property = section.properties[prop];
            if (isField(property)) {
                if (property.hidden && !showHiddenFields) {
                    continue;
                }
                total++;
                if (property.value === null) {
                    remaining++;
                }
            } else {
                const res = getFieldCount(property, showHiddenFields);
                total += res.total;
                remaining += res.remaining;
            }
        }
        return { total, remaining, completed: total - remaining };
    }

    if (Array.isArray(section.properties)) {
        /**
         * If section is WorksheetSchemaList, section.properties is `WorksheetSchemaListItem[]`
         * For other section types, section.properties is `{[key: string]: WorksheetSchemaSection | WorksheetSchemaSectionGroup | WorksheetSchemaField}`
         * We need to convert them to a same type, to loop through and calculate the fields
         */
        allProperties = section.properties.map((p) => p.properties);
    } else {
        allProperties = [section.properties];
    }
    for (const properties of allProperties) {
        for (const key in properties) {
            const property = properties[key];

            if (isField(property)) {
                if (property.hidden && !showHiddenFields) {
                    continue;
                }
                total++;
                if (property.value === null) {
                    remaining++;
                }
            } else {
                const res = getFieldCount(property, showHiddenFields);
                total += res.total;
                remaining += res.remaining;
            }
        }
    }

    return { total, remaining, completed: total - remaining };
}

/**
 * Function to sort and map schema properties
 *
 * @param properties
 */
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function sortByOrder<T extends WorksheetSchemaBase>(properties: T[] | Record<string, T>) {
    return Object.entries(properties)
        .sort((a, b) => (a[1].order > b[1].order ? 1 : -1))
        .map(([, value]) => value);
}

/**
 * Check if section group is at the top level
 * e.g level 1. -> IS top level
 * e.g level 1.1 -> IS NOT top level
 * e.g level 2.3 -> IS NOT top level
 *
 * @param level
 * @returns True if section group is at top level, else false
 */
export function isTopLevel(level: string): boolean {
    return !level.includes('.');
}

/**
 * Go through template provided to update id and add in value field
 *
 * @param template Template for new list item
 * @param index Index of where list item is placed within the current list item array
 * @returns { [key: string]: WorksheetSchemaSection | WorksheetSchemaSectionGroup | WorksheetSchemaTable}
 */
function fillProperties(
    template: { [key: string]: CWTSchemaSection | CWTSchemaSectionGroup | CWTSchemaTable | CWTSchemaField },
    listId: string,
    index: number
): {
    [key: string]:
        | WorksheetSchemaSection
        | WorksheetSchemaSectionGroup
        | WorksheetSchemaTable
        | WorksheetSchemaField
        | WorksheetSchemaTable
        | (WorksheetSchemaField & { styles: TableRowStyles });
} {
    const properties: {
        [key: string]: WorksheetSchemaSection | WorksheetSchemaSectionGroup | WorksheetSchemaTable | WorksheetSchemaField;
    } = {};

    for (const propKey in template) {
        const property = template[propKey];

        if (property.type === SectionType.SECTION) {
            const section: { [key: string]: WorksheetSchemaField } = {};

            for (const fieldKey in property.properties) {
                const field = property.properties[fieldKey];

                // @ts-ignore
                section[fieldKey] = {
                    ...field,
                    value: field.defaultValue,
                    reconciliation: field.reconciliation ? { ...field.reconciliation, entryId: null } : null,
                    id: formatTemplateListItemId(field.id, index),
                    dependentFields: [...field.dependentFields].map((df) => ({ ref: df.ref.replace('.item', `[${index}]`) })),
                    calculation: formatCollectionCalculation(field.calculation, index),
                };
            }

            let summary = property.summary?.properties[getReference(property.id)] || null;

            if (summary) {
                summary = formatPropertySummary(summary, index);
            }

            properties[propKey] = {
                ...property,
                id: formatTemplateListItemId(property.id, index),
                properties: section,
                summary: property.summary ? { properties: { [getReference(property.id)]: summary } } : null,
            };
        } else if (property.type === SectionType.SECTION_GROUP) {
            properties[propKey] = {
                ...property,
                id: formatTemplateListItemId(property.id, index),
                properties: fillProperties(property.properties, listId, index),
            };
        } else if (property.type === TableType.TABLE) {
            const rowProperties = {};

            Object.keys(property.properties.row.properties).forEach((key) => {
                const field = property.properties.row.properties[key];

                // @ts-ignore
                rowProperties[key] = {
                    ...field,

                    id: formatTemplateListItemId(field.id, index),
                    dependentFields: [...field.dependentFields].map((df) => {
                        const r = { ref: df.ref };

                        let found = false;
                        const accessors = ['.__self__', '.__prev__', '.__next__'];
                        accessors.forEach((accessor) => {
                            if (!found) {
                                r.ref = r.ref.replace(accessor, () => {
                                    found = true;

                                    if (accessor === '.__self__') {
                                        return `[${index}]`;
                                    } else if (accessor === '.__prev__') {
                                        return `[${index - 1}]`;
                                    } else {
                                        return `[${index + 1}]`;
                                    }
                                });
                            }
                        });

                        return r;
                    }),
                    calculation: traverse(structuredClone(field.calculation)).forEach(function (x) {
                        if (isRef(x)) {
                            let found = false;
                            const accessors = ['.__self__', '.__prev__', '.__next__'];

                            accessors.forEach((accessor) => {
                                if (!found) {
                                    x.ref = x.ref.replace(accessor, () => {
                                        found = true;
                                        if (accessor === '.__self__') {
                                            return `[${index}]`;
                                        } else if (accessor === '.__prev__') {
                                            return `[${index - 1}]`;
                                        } else {
                                            return `[${index + 1}]`;
                                        }
                                    });
                                }
                            });
                        }
                    }),
                };
            });

            const summaryProperties = {};
            if (property.summary?.properties) {
                Object.keys(property.summary.properties).forEach((key) => {
                    const field = property.summary!.properties[key];
                    if (!field) {
                        return;
                    }
                    // @ts-ignore
                    summaryProperties[key] = formatPropertySummary(field, index);
                });
            }

            const tbl: WorksheetSchemaTable = {
                ...property,
                generatedFromFunction: property.generatedFromFunction
                    ? traverse(structuredClone(property.generatedFromFunction)).forEach(function (x) {
                          if (isRef(x)) {
                              let found = false;
                              const accessors = ['.__self__', '.__prev__', '.__next__'];

                              accessors.forEach((accessor) => {
                                  if (!found) {
                                      x.ref = x.ref.replace(accessor, () => {
                                          found = true;
                                          if (accessor === '.__self__') {
                                              return `[${index}]`;
                                          } else if (accessor === '.__prev__') {
                                              return `[${index - 1}]`;
                                          } else {
                                              return `[${index + 1}]`;
                                          }
                                      });
                                  }
                              });
                          }
                      })
                    : null,
                row: {
                    ...property.properties.row,
                    id: formatTemplateListItemId(property.properties.row.id, index),
                    properties: rowProperties,
                },
                summary: property.summary ? { properties: summaryProperties } : null,
                properties: [],
                id: formatTemplateListItemId(property.id, index),
            };

            const minRows = property.config?.minRows ?? 0;

            for (let i = 0; i < minRows; i++) {
                const r = buildNewRow(tbl);
                tbl.properties.push(r);
            }

            // @ts-ignore
            properties[propKey] = tbl;
        } else if (isCWTSchemaField(property)) {
            properties[propKey] = {
                ...property,
                // @ts-ignore
                value: property.defaultValue,
                id: formatTemplateListItemId(property.id, index),
                dependentFields: formatDependentFields(property.dependentFields, index),
                calculation: formatCollectionCalculation(property.calculation, index),
            };
        }
    }

    return properties;
}

function formatPropertySummary(summary: CWTSchemaPropertySummary, index: number): CWTSchemaPropertySummary {
    return {
        ...summary,
        id: formatTemplateListItemId(summary.id, index),
        dependentFields: [...summary.dependentFields].map((df) => ({ ref: df.ref.replace('.__self__', `[${index}]`) })),
        calculation: traverse(structuredClone(summary.calculation)).forEach(function (x) {
            if (isRef(x)) {
                this.update({ ref: x.ref.replace('.__self__', `[${index}]`) });
            }
        }),
    };
}

function fillRowProperties(
    row: CWTSchemaTableRow,
    index: number,
    rowId: string,
    tableId: string
): { [key: string]: WorksheetSchemaField & { styles: TableRowStyles } } {
    return Object.keys(row.properties).reduce<{ [key: string]: WorksheetSchemaField & { styles: TableRowStyles } }>((acc, key) => {
        const field = row.properties[key];

        // @ts-ignore
        acc[key] = {
            ...field,
            pinned: false,
            overridden: false,
            value: field.defaultValue ?? null,
            id: `${rowId}.properties.${key}`,
            reconciliation: field.reconciliation ? { ...field.reconciliation, entryId: null } : null,
            dependentFields: formatDependentFields(field.dependentFields, index),
            calculation: formatCollectionCalculation(field.calculation, index),
        };
        return acc;
    }, {});
}

function formatDependentFields(dependentFields: CWTRef[], index: number): CWTRef[] {
    return dependentFields.map((df: CWTRef) => {
        let str = df.ref;
        str = str.replaceAll('.__prev__', `[${index - 1}]`);
        str = str.replaceAll('.__self__', `[${index}]`);
        str = str.replaceAll('.item.', `[${index}].`);
        str = str.replaceAll('.__next__', `[${index + 1}]`);
        return {
            ref: str,
        };
    });
}

function formatCollectionCalculation(calculation: CWTSchemaCalculation | null, index: number): CWTSchemaCalculation | null {
    if (!calculation) {
        return null;
    }

    const clone = traverse(calculation).clone();

    traverse(clone).forEach(function (x) {
        if (isRef(x)) {
            x.ref = x.ref.replaceAll('.__prev__', `[${index - 1}]`);
            x.ref = x.ref.replaceAll('.__self__', `[${index}]`);
            x.ref = x.ref.replaceAll('.__next__', `[${index + 1}]`);
        }
    });

    return clone;
}

/**
 * Append new list item into current schema form based off template
 *
 * @param schema WorksheetSchema
 * @param template Template for WorksheetSchemaListItem
 * @param listId List id
 */
export function addListItem(schema: WorksheetSchema, listId: string): WorksheetSchemaListItem {
    // Get the current number of list items for this list
    const list = getProperty(schema, listId) as WorksheetSchemaList;
    const listItemLength = (list.properties || []).length;

    const item = current(list.item);

    // Build the new list item
    const newListItem: WorksheetSchemaListItem = {
        id: formatTemplateListItemId(item.id, listItemLength),
        name: `${list.name} ${listItemLength + 1}`,
        order: listItemLength + 1,
        description: list.description,
        readOnly: false,
        type: ListType.LIST_ITEM,
        // Properties are recursively built
        properties: fillProperties(item.properties, item.id, listItemLength),
    };

    // Update schema list with latest list items
    set(schema, `${list.id}.properties`, [...list.properties, newListItem]);

    return newListItem;
}

/**
 * Replace .item in template list item id
 *
 * e.g:
 *   - List id is properties.assets
 *   - Id for a template list item is properties.item
 *   - When being appended to the current list item array with 3 items
 *   - Id for the list item should become properties.assets.properties[3]
 *
 * @param id Template list item id
 * @param index Index for list item
 * @returns Formatted id
 */
function formatTemplateListItemId(id: string, index: number): string {
    return `${id.replace('.item', `[${index}]`)}`;
}

/**
 * Remove list item from a list
 *
 * @param schema WorksheetSchema
 * @param id List item id to remove
 */
export function removeListItem(schema: WorksheetSchema, id: string): void {
    const listId = (id.split('.') || []).slice(0, -1).join('.');
    const list = getProperty(schema, listId);

    if (!list || !isList(list)) {
        return;
    }
    const listProperties = list.properties as WorksheetSchemaListItem[];

    const filteredProperties = listProperties.filter((p) => p.id !== id);
    const reorderedProperties = reorder(list.id, filteredProperties);

    set(schema, `${listId}.properties`, reorderedProperties);
}

/**
 * Reorder list items within array
 *
 * @param listItems New list item array
 * @param listId List id
 * @returns Sorted WorksheetSchemaListItem[]
 */
export function reorder<T extends WorksheetSchemaListItem | WorksheetSchemaTableRow>(
    collectionId: string,
    items: T[]
): (WorksheetSchemaListItem | WorksheetSchemaTableRow)[] {
    let order = 1;

    // order the list items by their order property ascending
    const orderedItems = items.sort((a, b) => a.order - b.order);

    return orderedItems.map((item) => {
        let newItem = current(item);
        // Skip over items that are already in the correct order
        // e.g if we have 3 items and item 2 is deleted, item 1 will still be in the correct order
        if (order !== item.order) {
            const orderDiff = order - item.order;
            const newOrder = newItem.order + orderDiff;
            const startId = item.id.substring(0, collectionId.length + 1);
            const itemId = item.id.substring(collectionId.length + 1);

            newItem = {
                ...item,
                order: newOrder,
                // Match any `[x]` pattern where x is a number
                id: `${startId}${itemId.replaceAll(/\[[0-9]+\]/g, `[${newOrder - 1}]`)}`,
            };

            // Update all child properties
            traverse(newItem.properties).forEach(function (x) {
                if (isProperty(x)) {
                    const startId = x.id.substring(0, collectionId.length + 1);
                    const itemId = x.id.substring(collectionId.length + 1);

                    x.id = `${startId}${itemId.replace(/\[[0-9]+\]/, `[${newOrder - 1}]`)}`;
                } else if (isRef(x)) {
                    // Get number
                    const startId = x.ref.substring(0, collectionId.length + 1);
                    const itemId = x.ref.substring(collectionId.length + 1);
                    const match = itemId.match(/\[([0-9]+)\]/);

                    if (match) {
                        const parsed = Number.parseInt(match[1]);
                        if (!isNaN(parsed)) {
                            const diff = parsed + orderDiff;
                            x.ref = `${startId}${itemId.replace(/\[[0-9]+\]/, `[${diff}]`)}`;
                        }
                    }
                }
                this.update(x);
            });
        }
        order++;

        return newItem;
    });
}

/**
 * Checks if a field belongs to a collection
 * @param schema Schema to check against
 * @param field Field to check for
 * @returns The collection the field belongs to, or null if the field is not part of a collection
 */
export function isInCollection(
    schema: WorksheetSchema,
    field: WorksheetSchemaProperty | CWTSchemaPropertySummary | string,
    highestCollection = false
): WorksheetSchemaList | WorksheetSchemaTable | null {
    const id = typeof field === 'string' ? field : field.id;

    const nodes = id.split('.');

    let node = nodes[0];

    let collection: WorksheetSchemaList | WorksheetSchemaTable | null = null;
    for (const n of nodes.slice(1)) {
        const property = getProperty(schema, node);

        if (property && (isList(property) || isTable(property))) {
            if (highestCollection) {
                return property;
            }
            collection = property;
        }
        node += `.${n}`;
    }

    return collection;
}

export function buildNewRow(table: WorksheetSchemaTable): WorksheetSchemaTableRow {
    const row = table.row;
    const rowCount = table.properties.length;
    const order = rowCount + 1;

    const rowId = `${table.id}.properties[${rowCount}]`;

    return {
        id: rowId,
        name: `${table.name} ${order}`,
        order,
        description: row.description,
        readOnly: false,
        type: TableType.TABLE_ROW,
        properties: fillRowProperties(row, rowCount, rowId, table.id),
    };
}

/**
 * Build reference map with all references within the template
 *
 * @param schema CWTSchema
 * @returns ReferenceMap
 */
export function buildReferenceMap(schema: WorksheetSchema): ReferenceMap {
    const references: ReferenceMap = {};

    traverse(schema).forEach(function (s) {
        // Only process properties and the type is not a list item
        if (isProperty(s) && s.type !== ListType.LIST_ITEM && s.type !== TableType.TABLE_ROW) {
            const reference = getReference(s.id);
            references[reference] = true;
        }
    });

    return references;
}

type BuildNewFieldInput = {
    section: WorksheetSchemaSection;
    name: string;
    reference: string;
    type: FieldType;
    value: string | number | null;
};

/**
 * Build new additional field for a section
 *
 * @param section WorksheetSchemaField
 */
export function buildNewAdditionalField({ section, name, reference, type, value }: BuildNewFieldInput): WorksheetSchemaField {
    const fieldId = `${section.id}.properties.${reference}`;

    // Add section summary as field dependent fields if there is a section summary on the same field type
    const addFieldToSummary = section.summary?.properties[getReference(section.id)]?.type === type;

    return {
        id: fieldId,
        name,
        description: null,
        helperText: null,
        order: Object.keys(section.properties).length + 1,
        readOnly: false,
        // @ts-ignore
        type,
        pinned: false,
        isAdditionalField: true,
        overridden: false,
        defaultValue: null,
        value,
        dependentFields: addFieldToSummary ? [{ ref: `#${section.id}.summary.properties.${getReference(section.id)}` }] : [],
        calculation: null,
    };
}

/**
 * Remove deleted additional field from section summary
 *
 * @param summary CWTSchemaPropertySummary
 * @param fieldId Field id
 */
export function removeFieldFromSectionSummary(summary: CWTSchemaPropertySummary, fieldId: string): CWTSchemaPropertySummary {
    const updatedParams = summary.calculation.return.params.filter((p) => (isRef(p) && p.ref.substring(1) !== fieldId) || !isRef(p));

    const newSummary: CWTSchemaPropertySummary = {
        ...summary,
        calculation: { ...summary.calculation, return: { ...summary.calculation.return, params: updatedParams as CWTRef[] } },
    };

    return newSummary;
}

/**
 * Add new additional field to section summary
 *
 * @param summary CWTSchemaPropertySummary
 * @param fieldId Field id
 */
export function addFieldToSectionSummary(summary: CWTSchemaPropertySummary, fieldId: string): CWTSchemaPropertySummary {
    const newSummary: CWTSchemaPropertySummary = {
        ...summary,
        calculation: {
            ...summary.calculation,
            return: { ...summary.calculation.return, params: [...summary.calculation.return.params, { ref: `#${fieldId}` }] as CWTRef[] },
        },
    };

    return newSummary;
}

/**
 * Checks if a condition object has the ConditionAnd structure.
 *
 * @param condition ConditionIf
 * @returns condition is ConditionAnd
 */
export function isConditionAnd(condition: ConditionIf): condition is ConditionAnd {
    return typeof condition === 'object' && 'and' in condition && Array.isArray(condition.and);
}

/**
 * Checks if a condition object has the ConditionOr structure.
 *
 * @param condition ConditionIf
 * @returns condition is ConditionOr
 */
export function isConditionOr(condition: ConditionIf): condition is ConditionOr {
    return typeof condition === 'object' && 'or' in condition && Array.isArray(condition.or);
}

function isObject(v: unknown): v is Record<string, unknown> {
    return typeof v === 'object' && !Array.isArray(v) && v !== null;
}

export function isRef(r: unknown): r is CWTRef {
    return isObject(r) && 'ref' in r;
}

export function isCalculation(c: unknown): c is CWTSchemaCalculation {
    return isObject(c) && 'type' in c && 'return' in c;
}

/**
 * Get pinned field within a section
 *
 * @param section WorksheetSchemaSection
 * @returns Pinned WorksheetSchemaField | null
 */
export function getPinnedField(section: WorksheetSchemaSection): WorksheetSchemaField | null {
    const fieldProperties = section.properties;

    for (const key of Object.keys(fieldProperties)) {
        const field = fieldProperties[key];
        if (field.pinned) {
            return field;
        }
    }

    return null;
}

/**
 * Get property reference from the id
 *
 * @param id Property id
 * @returns Property reference
 */
export function getReference(id: string): string {
    return (id.split('.') || []).slice(-1)[0];
}

type PropertyPath = {
    id: string;
    name: string;
    order: number;
    type: ListType | SectionType | TableType | FieldType;
    summary: CWTSchemaPropertiesSummary | null;
};

/**
 * Build full path for a property
 *
 * @param schema WorksheetSchema
 * @param property WorksheetSchemaProperty
 * @returns An array of path consisting of all parent properties that lead to actual property
 */
export function buildPropertyPath(schema: WorksheetSchema, property: WorksheetSchemaProperty): PropertyPath[] {
    // Get number of levels for a property (each level has a corresponding id of 'properties.reference')
    const totalPathLevels = property.id.split('.').filter((ref) => ref === 'properties').length;

    const paths: PropertyPath[] = [];

    let i = totalPathLevels - 1;

    /**
     * Go through the full property path, get the actual parent property starting from the top parent (LIST property)
     * Path for each level is made up of 'properties.xxx', hence we slice the id array by doing slice(0, -6) then slice(0, -4)...
     *
     * E.g If a field is made up of 4 levels with the id of 'properties.list1.properties.item.properties.section1.properties.field1'
     *     We will first get the full property of 'properties.list1' then 'properties.list1.properties.section1'
     *     then 'properties.list1.properties.section1.properties.item' then 'properties.list1.properties.section1.properties.item.properties.field1'
     */
    while (i > 0) {
        const parentPropertyId = property.id
            .split('.')
            .slice(0, i * -2)
            .join('.');

        const parentProperty = getProperty(schema, parentPropertyId);

        if (parentProperty) {
            paths.push({
                id: parentProperty.id,
                name: parentProperty.name,
                type: parentProperty.type,
                order: parentProperty.order,
                summary: 'summary' in parentProperty ? parentProperty.summary : null,
            });
        }

        i--;
    }

    // Includes the actual property at the end of array
    paths.push({
        id: property.id,
        name: property.name,
        type: property.type,
        order: property.order,
        summary: 'summary' in property ? property.summary : null,
    });

    return paths;
}

/**
 * Format list summary into an array of fake section groups/sections
 *
 * 1. Run buildSectionFromSummarisedProperty(schema, list)
 *   - Get summarised property in the list for each list item, group them up into a fake section property
 *   - Section is the actual summarised property, field properties in the section are made up of field in each list item
 *
 * 2. For every fake section created, wrap it by a section group parent property if it belongs in a section group
 *
 * @param schema WorksheetSchema
 * @param list WorksheetSchemaList
 * @returns An array with WorksheetSchemaSection or WorksheetSchemaSectionGroup
 */
export function formatListSummary(
    schema: WorksheetSchema,
    list: WorksheetSchemaList
): (WorksheetSchemaSection | WorksheetSchemaSectionGroup)[] {
    // Build fake sections from fields in list summary
    let summarisedSections = buildSectionFromSummarisedProperty(schema, list);

    const sectionGroups: { [key: string]: WorksheetSchemaSectionGroup } = {};

    for (const section of summarisedSections) {
        const property = getProperty(schema, section.id.replace('.item.', '[0].'));

        if (property === null) {
            continue;
        }

        const propertyPaths = buildPropertyPath(schema, property);
        const sectionGroupParentProperty = propertyPaths.find((p) => p.type === SectionType.SECTION_GROUP);

        if (sectionGroupParentProperty) {
            const sectionGroupRef = getReference(sectionGroupParentProperty.id);
            const sectionRef = getReference(section.id);

            // If section group already exists with section properties, push section into existing section group
            if (sectionGroups[sectionGroupRef]) {
                sectionGroups[sectionGroupRef] = {
                    ...sectionGroups[sectionGroupRef],
                    properties: {
                        ...sectionGroups[sectionGroupRef].properties,
                        [sectionRef]: {
                            ...section,
                            order: propertyPaths.find((p) => getReference(p.id) === sectionRef)?.order || section.order,
                        },
                    },
                };
            }
            // If section group doesn't exist, create new section group with section properties
            else {
                sectionGroups[sectionGroupRef] = {
                    ...sectionGroupParentProperty,
                    id: sectionGroupParentProperty.id.replace('[0].', '.item.'),
                    description: null,
                    readOnly: true,
                    type: SectionType.SECTION_GROUP,
                    properties: {
                        [sectionRef]: {
                            ...section,
                            order: propertyPaths.find((p) => getReference(p.id) === sectionRef)?.order || section.order,
                        },
                    },
                };
            }

            summarisedSections = summarisedSections.filter((s) => s.id !== section.id);
        }
    }

    return [...Object.values(sectionGroups), ...summarisedSections];
}

/**
 * Build fake section for each list summary property
 *
 * 1. Get actual summarised property id from the calculation params
 * 2. Format summarised property id to be the actual section id or field id (replace .__every__. and remove unnecessary suffix in id)
 * 3. Build a fake section parent property wrapper
 * 4. Loop through list item, replace .item. with actual list item index to get the corresponding property in each list item
 * 5. Add the property from each list item as the field properties within the fake section
 * 6. Return fake sections created for each list summary property
 *
 * @param schema WorksheetSchema
 * @param list WorksheetSchemaList
 * @returns WorksheetSchemaSection[]
 */
function buildSectionFromSummarisedProperty(schema: WorksheetSchema, list: WorksheetSchemaList): WorksheetSchemaSection[] {
    if (!list.summary) {
        return [];
    }

    const summarisedPropertyKeys = Object.keys(list.summary.properties).filter((key) => list.summary?.properties[key] !== null);
    const summarisedSections: WorksheetSchemaSection[] = [];

    for (const key of summarisedPropertyKeys) {
        const listSummaryItem = list.summary.properties[key] as CWTSchemaPropertySummary;

        let summarisedPropertyId = '';

        /**
         * Calculation params of the list summary returns the actual property of the field to be summarised
         *
         * E.g { "ref": "#properties.list1.properties.__every__.properties.field1" }
         */
        traverse(listSummaryItem.calculation).forEach(function (x) {
            if (isRef(x)) {
                summarisedPropertyId = x.ref.replaceAll('.__every__.', '.item.').substring(1);
            }
        });

        if (!summarisedPropertyId) {
            continue;
        }

        /**
         * Id of the fake section should be the actual section id if the summarised property is a pinned field or a section summary value
         *
         * E.g if summarisedPropertyId = 'properties.list1.properties.item.properties.section1.properties.summary.section1', we should remove the end part 'properties.summary.section1'
         * E.g if summarisedPropertyId = 'properties.list1.properties.item.properties.section1.properties.sectionField1', we should remove the end part 'properties.sectionField1'
         */
        let sectionId = summarisedPropertyId;
        if (sectionId.includes('summary')) {
            sectionId = sectionId.split('.').slice(0, -3).join('.');
        }
        if (getReference(sectionId) !== getReference(listSummaryItem.id)) {
            sectionId = sectionId.split('.').slice(0, -2).join('.');
        }

        /**
         * Build fake section wrapper with actual summarised property
         * Actual summarised property will become the summary field of the section
         * Section properties will be made up of field in each list item
         */
        const section: WorksheetSchemaSection = {
            id: sectionId,
            name: listSummaryItem.name,
            order: list.item.properties[key]?.order || 1,
            description: null,
            readOnly: true,
            type: SectionType.SECTION,
            properties: {},
            summary: { properties: { [getReference(listSummaryItem.id)]: listSummaryItem } },
            additionalFields: null,
        };

        /**
         * Fill up section properties with field in each list item
         * Replace .item. with list item index to get the field in each list item
         * Build field to add to the fake section
         */
        for (let i = 0; i < list.properties.length; i++) {
            const actualPropertyId = summarisedPropertyId.replace('.item.', `[${i}].`);
            const childProperty = getProperty(schema, actualPropertyId);

            if (childProperty && isField(childProperty)) {
                const field: WorksheetSchemaField = {
                    ...childProperty,
                    name: list.properties[i].name,
                    order: i + 1,
                    description: null,
                    readOnly: true,
                    helperText: null,
                    defaultValue: null,
                    pinned: false,
                    overridden: false,
                    isAdditionalField: false,
                };

                section.properties[i] = field;
            }
        }

        summarisedSections.push(section);
    }

    return summarisedSections;
}
